import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plane, Mail, Lock, User, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api';

export const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

const GoogleIcon = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const PasswordInput = ({ id, value, onChange, placeholder, testId, autoComplete }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      <Input data-testid={testId} id={id} type={show ? 'text' : 'password'} value={value} onChange={onChange} placeholder={placeholder} required minLength={8} autoComplete={autoComplete} className="border-2 pl-10 pr-10" />
      <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" aria-label={show ? 'Hide password' : 'Show password'}>
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
};

const Login = ({ onAuth }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const payload = mode === 'register' ? form : { email: form.email, password: form.password };
      const { data } = await api.post(`/auth/${mode}`, payload);
      onAuth?.(data);
      toast.success(mode === 'register' ? `Welcome to TripSoul, ${data.name.split(' ')[0]}!` : `Welcome back, ${data.name.split(' ')[0]}!`);
      navigate('/dashboard', { state: { user: data }, replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center px-4 py-24">
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-20 left-20 w-72 h-72 bg-teal-400 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-cyan-400 rounded-full blur-3xl"></div>
      </div>

      <Card className="w-full max-w-md border-0 shadow-2xl relative z-10" data-testid="login-page">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <Link to="/" className="inline-flex w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full items-center justify-center mb-4">
              <Plane className="w-8 h-8 text-white" />
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to TripSoul</h1>
            <p className="text-gray-600">Sign in to manage your bookings and explore destinations</p>
          </div>

          <Button
            data-testid="google-login-btn"
            onClick={handleGoogleLogin}
            className="w-full bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-200 py-6 text-lg font-semibold flex items-center justify-center space-x-3"
          >
            <GoogleIcon />
            <span>Continue with Google</span>
          </Button>

          <div className="flex items-center gap-3 my-6">
            <span className="flex-1 h-px bg-gray-200" />
            <span className="text-xs uppercase tracking-wider text-gray-400">or with email</span>
            <span className="flex-1 h-px bg-gray-200" />
          </div>

          <Tabs value={mode} onValueChange={(m) => { setMode(m); setError(''); }} className="mb-5">
            <TabsList className="grid grid-cols-2 w-full h-11">
              <TabsTrigger data-testid="auth-tab-login" value="login">Login</TabsTrigger>
              <TabsTrigger data-testid="auth-tab-register" value="register">Create account</TabsTrigger>
            </TabsList>
          </Tabs>

          <form onSubmit={submit} className="space-y-4" data-testid={`${mode}-form`}>
            {mode === 'register' && (
              <div>
                <Label htmlFor="name" className="mb-1.5 block">Full name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input data-testid="auth-name-input" id="name" value={form.name} onChange={set('name')} placeholder="Your name" required minLength={2} autoComplete="name" className="border-2 pl-10" />
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="email" className="mb-1.5 block">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input data-testid="auth-email-input" id="email" type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" required autoComplete="email" className="border-2 pl-10" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label htmlFor="password">Password</Label>
                {mode === 'login' && (
                  <Link data-testid="forgot-password-link" to="/forgot-password" className="text-xs text-teal-600 hover:underline">Forgot password?</Link>
                )}
              </div>
              <PasswordInput id="password" testId="auth-password-input" value={form.password} onChange={set('password')} placeholder={mode === 'register' ? 'At least 8 characters' : 'Your password'} autoComplete={mode === 'register' ? 'new-password' : 'current-password'} />
            </div>

            {error && <p data-testid="auth-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">{error}</p>}

            <Button data-testid="auth-submit-btn" type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white py-5 text-base font-semibold">
              {submitting ? 'Please wait...' : mode === 'register' ? 'Create account' : 'Login'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-500">
            <p>By signing in, you agree to our Terms of Service and Privacy Policy</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
